import { useLiveQuery } from 'dexie-react-hooks';
import { ArrowLeft, Delete, Leaf } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { accessProblem, type StaffMember } from '@sense/shared';
import { autoLockMinutes, useCashier } from '../lib/cashier';
import { db } from '../lib/db';
import { verifyPin } from '../lib/pin';
import { useSession } from '../lib/session';
import { SyncPill } from './SyncPill';
import { tr } from '../lib/i18n';

const TINTS = ['bg-t-green text-t-green-fg', 'bg-t-amber text-t-amber-fg', 'bg-t-blue text-t-blue-fg', 'bg-t-rose text-t-rose-fg', 'bg-t-teal text-t-teal-fg', 'bg-t-violet text-t-violet-fg'];
const initials = (n: string) => n.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
const MAX_TRIES = 5;
const COOLDOWN_MS = 30_000;

/** Lock the till after N idle minutes (no key, pointer or touch). */
export function useAutoLock(enabled: boolean) {
  const lock = useCashier((s) => s.lock);
  const active = useCashier((s) => s.active);
  useEffect(() => {
    const minutes = autoLockMinutes();
    if (!enabled || !active || minutes <= 0) return;
    let timer = setTimeout(lock, minutes * 60_000);
    const bump = () => { clearTimeout(timer); timer = setTimeout(lock, minutes * 60_000); };
    const events = ['keydown', 'pointerdown', 'touchstart', 'wheel'] as const;
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => { clearTimeout(timer); events.forEach((e) => window.removeEventListener(e, bump)); };
  }, [enabled, active, lock]);
}

export function LockScreen() {
  // Staff outside their allowed hours, past their access date or switched off can't unlock (works offline).
  const staff = useLiveQuery(() => db.staff.filter((s) => s.active && !!s.pin_hash
    && (s.role === 'owner' || !accessProblem({ exp: s.access_expires_at, tz: 330, days: s.access_schedule }))).toArray(), []);
  const me = useSession((s) => s.me);
  const signOut = useSession((s) => s.signOut);
  const unlock = useCashier((s) => s.unlock);
  const [who, setWho] = useState<StaffMember | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [tries, setTries] = useState(0);
  const [until, setUntil] = useState(0);
  const [now, setNow] = useState(Date.now());

  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  // Only one person on this till? Skip straight to their PIN.
  useEffect(() => { if (staff?.length === 1 && !who) setWho(staff[0]); }, [staff, who]);
  const cooling = until > now;

  const submit = useCallback(async (value: string) => {
    if (!who || checking || cooling) return;
    setChecking(true);
    const ok = await verifyPin(value, who.pin_hash!, who.pin_salt!);
    setChecking(false);
    if (ok) { setTries(0); unlock(who); return; }
    const n = tries + 1;
    setTries(n);
    setPin('');
    if (n >= MAX_TRIES) { setUntil(Date.now() + COOLDOWN_MS); setTries(0); setError(`Too many tries. Wait 30 seconds.`); }
    else setError(`Wrong PIN. ${MAX_TRIES - n} ${MAX_TRIES - n === 1 ? 'try' : 'tries'} left.`);
    navigator.vibrate?.([30, 40, 30]);
  }, [who, checking, cooling, tries, unlock]);

  const press = useCallback((d: string) => {
    if (cooling) return;
    setError(null);
    const next = (pin + d).slice(0, 6);
    setPin(next);
    if (next.length === 6) void submit(next);
  }, [cooling, submit, pin]);

  useEffect(() => {
    if (!who) return;
    const onKey = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) { e.preventDefault(); press(e.key); }
      else if (e.key === 'Backspace') { e.preventDefault(); setPin((p) => p.slice(0, -1)); }
      else if (e.key === 'Enter') { e.preventDefault(); if (pin.length >= 4) void submit(pin); }
      else if (e.key === 'Escape' && (staff?.length ?? 0) > 1) { setWho(null); setPin(''); setError(null); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [who, pin, press, submit, staff]);

  const time = new Date(now).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="flex min-h-app flex-col bg-forest-800 text-white lg:flex-row">
      <aside className="flex flex-col gap-2 px-6 pt-6 pb-4 lg:w-[40%] lg:justify-between lg:p-12">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-forest-700"><Leaf /></span>
          <div><div className="text-lg font-bold">{me?.store.name}</div><div className="text-sm text-white/70">{tr('{0} · Terminal {1}', { 0: me?.store.code, 1: me?.device.code })}</div></div>
        </div>
        <div className="hidden lg:block">
          <div className="num text-7xl font-semibold">{time}</div>
          <div className="mt-2 text-white/70">{new Date(now).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
        </div>
        <div className="hidden lg:block"><SyncPill /></div>
      </aside>

      <main className="flex flex-1 flex-col items-center justify-center rounded-t-[28px] bg-paper px-5 py-8 text-ink lg:rounded-none">
        {!who ? (
          <div className="w-full max-w-xl">
            <h1 className="text-center text-2xl font-bold">{tr('Who’s billing?')}</h1>
            <p className="mt-1 text-center text-sm text-muted">{tr('Tap your name, then enter your PIN.')}</p>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {staff?.map((s, i) => (
                <button key={s.id} onClick={() => { setWho(s); setPin(''); setError(null); }}
                  className="flex flex-col items-center gap-2 rounded-3xl border border-line bg-white p-4 hover:border-forest-600 hover:shadow-md">
                  <span className={`flex h-14 w-14 items-center justify-center rounded-full text-lg font-bold ${TINTS[i % TINTS.length]}`}>{initials(s.name)}</span>
                  <span className="font-bold">{s.name}</span>
                  <span className="text-xs text-muted capitalize">{s.role.replace('_', ' ')}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex w-full max-w-xs flex-col items-center">
            {(staff?.length ?? 0) > 1 && (
              <button onClick={() => { setWho(null); setPin(''); setError(null); }} className="mb-4 flex items-center gap-1 self-start text-sm font-bold text-primary"><ArrowLeft size={16} />{tr('Not {0}?', { 0: who.name })}</button>
            )}
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-t-green text-xl font-bold text-primary">{initials(who.name)}</span>
            <h1 className="mt-3 text-xl font-bold">{tr('Hi {0}, enter your PIN', { 0: who.name.split(' ')[0] })}</h1>
            <div className="mt-5 flex gap-3" aria-label={`${pin.length} digits entered`} role="status">
              {Array.from({ length: Math.max(4, pin.length) }, (_, i) => (
                <span key={i} className={`h-4 w-4 rounded-full ${i < pin.length ? 'bg-forest-700' : 'border-2 border-line'}`} />
              ))}
            </div>
            <div className="mt-3 h-5 text-sm font-bold text-danger" role="alert">{cooling ? tr('Too many tries. Wait {0} s.', { 0: Math.ceil((until - now) / 1000) }) : error}</div>
            <div className="mt-3 grid w-full grid-cols-3 gap-3">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                <button key={d} disabled={cooling} onClick={() => press(d)} className="num h-16 rounded-full bg-white text-2xl font-semibold shadow-card active:bg-forest-50 disabled:opacity-40">{d}</button>
              ))}
              <button aria-label="Delete digit" onClick={() => setPin((p) => p.slice(0, -1))} className="flex h-16 items-center justify-center rounded-full text-muted"><Delete /></button>
              <button disabled={cooling} onClick={() => press('0')} className="num h-16 rounded-full bg-white text-2xl font-semibold shadow-card active:bg-forest-50 disabled:opacity-40">0</button>
              <button disabled={pin.length < 4 || checking || cooling} onClick={() => void submit(pin)} className="h-16 rounded-full bg-forest-700 text-base font-bold text-white disabled:opacity-50">{checking ? '…' : 'OK'}</button>
            </div>
          </div>
        )}
        <button onClick={() => { if (confirm(tr('Sign out this till? An owner or manager will need to sign in again.'))) void signOut(); }}
          className="mt-8 text-sm font-bold text-muted hover:text-ink">{tr('Sign in with code or email instead')}</button>
      </main>
    </div>
  );
}
